import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Legg i kurv uten å forlate siden.
 *
 * PDP-ene og pakkesiden har egne, rene `/cart/add`-skjemaer i stedet for Horizons
 * `product-form-component`. Uten JavaScript poster de vanlig og lander på kurvsiden.
 * Med dette skriptet fanges submit på skjemaer merket `data-skm-kurv-skuff`: varen
 * legges i kurven via fetch, og en CartAddEvent oppdaterer kurvboblen og skuffa.
 * Skuffa åpner seg selv når temainnstillingen `auto_open_cart_drawer` er på.
 *
 * Avviser Shopify varen (f.eks. lagergrense), vises meldingen under skjemaet, og
 * kurvboblen og skuffa oppdateres med det som faktisk ble lagt inn. Ved uleselig svar
 * kontrolleres kurven før skuffa oppdateres. Et usikkert POST gjentas aldri.
 */

const SKJEMA = 'form[data-skm-kurv-skuff]';

/**
 * @param {HTMLFormElement} form
 * @param {string} tekst
 */
function visFeil(form, tekst) {
  let melding = /** @type {HTMLElement | null} */ (form.querySelector('[data-skm-kurv-skuff-feil]'));
  if (!melding) {
    melding = document.createElement('p');
    melding.className = 'skm-kurv-skuff__feil';
    melding.setAttribute('data-skm-kurv-skuff-feil', '');
    melding.setAttribute('role', 'alert');
    form.append(melding);
  }
  melding.textContent = tekst;
  melding.hidden = !tekst;
}

/** @returns {Promise<number | undefined>} */
async function hentAntallIKurven() {
  try {
    const svar = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const kurv = await svar.json();
    return kurv.item_count;
  } catch {
    return undefined;
  }
}

/**
 * Ved lagergrense legger Shopify inn det som finnes og avviser resten, så kurven kan ha
 * endret seg selv om svaret er en feil. Kurvboblen og skuffa oppdateres med totalen fra
 * `/cart.js`, men skuffa åpnes ikke: meldingen ved skjemaet forklarer hva som skjedde, og
 * en åpen skuff ville dekket den på mobil.
 *
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 */
async function oppdaterEtterAvvisning(form, formData) {
  const antall = await hentAntallIKurven();
  if (antall === undefined) return;

  const skuff = document.querySelector('cart-drawer-component');
  const autoAapne = skuff?.hasAttribute('auto-open') ?? false;
  skuff?.removeAttribute('auto-open');
  try {
    form.dispatchEvent(
      new CartAddEvent({}, String(formData.get('id') ?? ''), {
        didError: true,
        source: 'skm-kurv-skuff',
        itemCount: antall,
        variantId: String(formData.get('id') ?? ''),
      })
    );
  } finally {
    if (autoAapne) skuff?.setAttribute('auto-open', '');
  }
}

/** @param {SubmitEvent} event */
async function leggIKurv(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches(SKJEMA)) return;

  event.preventDefault();
  const formData = new FormData(form);
  const bis = form.matches('.skm-v2-buybox__form, .skm-v2-hero__mobilebar-form')
    ? form.closest('[data-skm-bis]') ?? form.closest('.skm-v2-hero')?.querySelector('[data-skm-bis]')
    : null;
  if (bis instanceof HTMLElement) {
    const selected = bis.querySelector('input[name="id"]:checked, input[type="hidden"][name="id"]');
    if (!(selected instanceof HTMLInputElement) || formData.get('id') !== selected.value
      || selected.dataset.variantAvailable !== 'true') {
      event.stopImmediatePropagation();
      if (selected instanceof HTMLInputElement && selected.value !== bis.dataset.variantId) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', selected.value);
        window.location.assign(url.href);
      } else {
        const action = bis.querySelector('.skm-v2-buybox__cta');
        if (action instanceof HTMLElement) {
          action.scrollIntoView({ block: 'center' });
          action.focus({ preventScroll: true });
        }
      }
      return;
    }
  }
  if (form.getAttribute('aria-busy') === 'true') return;

  const knapper = form.querySelectorAll('button[type="submit"]');
  form.setAttribute('aria-busy', 'true');
  knapper.forEach((knapp) => knapp.setAttribute('disabled', ''));
  visFeil(form, '');

  const seksjoner = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);
  if (seksjoner.length) formData.append('sections', seksjoner.join(','));

  try {
    const svar = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData }));
    const data = await svar.json();

    if (data.status) {
      visFeil(form, data.description || data.message || '');
      await oppdaterEtterAvvisning(form, formData);
      return;
    }

    // Kurvboblen setter totalen når den får den, og legger til antallet når kilden er
    // Horizons produktskjema. Uten totalen faller vi tilbake til det siste.
    const antall = await hentAntallIKurven();
    form.dispatchEvent(
      new CartAddEvent({}, String(formData.get('id') ?? ''), {
        source: antall === undefined ? 'product-form-component' : 'skm-kurv-skuff',
        itemCount: antall ?? (Number(formData.get('quantity')) || 1),
        variantId: String(formData.get('id') ?? ''),
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error(error);
    const skuff = /** @type {import('./cart-drawer').CartDrawerComponent | null} */ (
      document.querySelector('cart-drawer-component')
    );
    if (skuff) await customElements.whenDefined('cart-drawer-component');
    if (!skuff || !(await skuff.recover())) {
      visFeil(form, 'Vi kunne ikke bekrefte tillegget. Sjekk handlekurven før du prøver igjen.');
    }
  } finally {
    form.removeAttribute('aria-busy');
    knapper.forEach((knapp) => knapp.removeAttribute('disabled'));
  }
}

// Capture-fasen: submit må avbrytes før Shopifys egen lytter på skjemaet ser den,
// ellers teller den «Added Product» i tillegg til /cart/add.js (to AddToCart per klikk).
document.addEventListener('submit', leggIKurv, true);
